import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { CalculateBalanceForm, TimeOffTypeDetails } from '@app/components';
import {
    DisplayDateSetting,
    ProfileBalanceDisplayServiceProvider,
    TimeOffProfileBalanceDisplayService,
} from '@app/components/time-off-policy-card/time-off-profile-balance-display.service';
import { LegendItem } from '@app/interfaces';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffRequestCalendarEvent } from '@app/models/time-off/time-off-request-calendar-event.model';
import { AuthService, NotifyService } from '@app/services';
import { TimeOffType } from '@models/time-off-v3/time-off-type.model';
import { isTimeOffBalanceDateToday } from '@time-off-v3/functions/settings';
import moment from 'moment';
import { TypeAndPolicyWithBalance } from '../../interfaces/time-off-balance.interface';
import { TimeOffDateFormatDashes, TimeUnit } from '../../meta/time-off-meta';
import { TimeOffRequestTable, TimeOffService } from '../../services';
import { TimeOffProfileMenuService } from '../../services/time-off-module-menu.service';
import { TimeOffTransactionTable } from '../../services/time-off-transaction-table/time-off-transaction.table';

type ProfileDialogType = 'DETAILS' | 'CALCULATE_BALANCE';

@Component({
    templateUrl: 'profile.template.html',
    styleUrls: ['profile.style.scss'],
    providers: [ProfileBalanceDisplayServiceProvider],
})
export class ProfileView implements OnInit {
    @ViewChild('calculateBalanceForm') calculateBalanceForm: CalculateBalanceForm;
    @ViewChild('timeOffTypeDetails') timeOffTypeDetails: TimeOffTypeDetails;

    isLoading = false;
    employee: Employee;
    timeOffTypeAndPolicyWithBalances: TypeAndPolicyWithBalance[];
    eventTypes = [TimeOffRequestCalendarEvent];
    requestTable: TimeOffRequestTable;
    transactionTable: TimeOffTransactionTable;

    selectedTimeOffTypeAndPolicyWithBalance: TypeAndPolicyWithBalance;

    calendarLegend: LegendItem[] = [];
    calendarPermission: string[] = Platform.timeOff.permission.calendar;
    endDate: string;
    timeOffTypes: TimeOffType[];
    hasTypesAssigned = false;
    modalHeight = '700px';
    balanceDateSetting: string = DisplayDateSetting.TODAY;
    currentlyOpenDialog?: ProfileDialogType;

    private timeOffBalanceDayIsToday: boolean;

    constructor(
        private timeOffService: TimeOffService,
        public auth: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private profileBalanceDisplayService: TimeOffProfileBalanceDisplayService,
        public tabMenuService: TimeOffProfileMenuService
    ) {
        this.employee = this.route.snapshot.data.models.get('employee');
    }

    async ngOnInit(): Promise<void> {
        await this.loadTimeOffTypesAndPoliciesWithBalances();

        const allAssignedTimeOffPolicies = this.employee.allAssignedTimeOffPolicies;
        this.timeOffTypes = allAssignedTimeOffPolicies.map((policy) => {
            policy.timeOffRequestRule.timeOffType = policy.timeOffType;

            return policy.timeOffType;
        });

        // If there is no time off types/policies assigned to the employee we stop here and show empty state
        if (!this.timeOffTypes.length) {
            return;
        }

        this.hasTypesAssigned = true;

        this.endDate = moment().format(TimeOffDateFormatDashes);

        this.requestTable = new TimeOffRequestTable(this.auth, this.timeOffTypes, this.employee.id);

        this.transactionTable = new TimeOffTransactionTable(
            this.auth,
            this.employee.id,
            this.endDate,
            this.timeOffTypes,
            allAssignedTimeOffPolicies
        );
    }

    async loadTimeOffTypesAndPoliciesWithBalances(): Promise<void> {
        try {
            this.isLoading = true;

            this.calendarLegend = this.employee.activeTimeOffPolicies.map((timeOffPolicy) => {
                return {
                    title: timeOffPolicy.timeOffType.name,
                    color: timeOffPolicy.timeOffType.color,
                };
            });

            this.timeOffBalanceDayIsToday = await isTimeOffBalanceDateToday(this.auth.company.id);

            this.balanceDateSetting = await this.profileBalanceDisplayService.balanceDateSetting;

            const timeOffTypeAndPolicyCombined: TypeAndPolicyWithBalance[] = await Promise.all(
                this.employee.timeOffPolicies.map(async (timeOffPolicy: TimeOffPolicy) => {
                    const timeOffBalance = await this.timeOffService.getBalance(this.employee.id, {
                        'filters[timeOffTypeId]': timeOffPolicy.timeOffType.id,
                        'filters[processedAt][to]':
                            await this.profileBalanceDisplayService.balanceDateFor(timeOffPolicy),
                    });

                    timeOffBalance.balanceDateLabel =
                        await this.profileBalanceDisplayService.balanceDateLabelFor(timeOffPolicy);

                    return { timeOffType: timeOffPolicy.timeOffType, timeOffBalance, timeOffPolicy };
                })
            );

            this.timeOffTypeAndPolicyWithBalances = timeOffTypeAndPolicyCombined;
            this.isLoading = false;
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
        }
    }

    getUnitOfTime(timeOffType: TimeOffType): TimeUnit {
        return timeOffType.displayInHours ? 'hours' : 'days';
    }

    viewCalculateBalanceModal(timeOffTypeId: number, carryoverCap: number): void {
        this.calculateBalanceForm.timeOffTypeId = timeOffTypeId;
        this.calculateBalanceForm.carryoverCap = carryoverCap;
        this.calculateBalanceForm.getBalanceOnDateSelect();
        this.currentlyOpenDialog = 'CALCULATE_BALANCE';
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    async viewDetails(timeOffTypeAndPolicyWithBalance: TypeAndPolicyWithBalance): Promise<void> {
        try {
            this.isLoading = true;

            // Set the timeOffTypeBalance
            this.selectedTimeOffTypeAndPolicyWithBalance = this.timeOffBalanceDayIsToday
                ? timeOffTypeAndPolicyWithBalance
                : await this.getTodayBalance(timeOffTypeAndPolicyWithBalance.timeOffPolicy);

            await this.timeOffTypeDetails.loadData(timeOffTypeAndPolicyWithBalance);

            this.currentlyOpenDialog = 'DETAILS';

            this.isLoading = false;
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err);
        }
    }

    async getTodayBalance(timeOffPolicy: TimeOffPolicy): Promise<TypeAndPolicyWithBalance> {
        const timeOffBalance = await this.timeOffService.getBalance(this.employee.id, {
            'filters[timeOffTypeId]': timeOffPolicy.timeOffTypeId,
        });

        return { timeOffType: timeOffPolicy.timeOffType, timeOffBalance, timeOffPolicy };
    }

    getBalanceAmount(typeWithBalance: TypeAndPolicyWithBalance): number {
        const { timeOffType, timeOffBalance } = typeWithBalance;

        return timeOffType.displayInHours ? timeOffBalance.balanceHours : timeOffBalance.balance;
    }

    createTimeOffRequest(): void {
        this.router.navigate(['/', 'time-off', 'v3', 'requests', 'create']);
    }
}
